import ActiveToggleButton from "@/components/ActiveToggleButton";
import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { SLAConfiguration } from "@/utils/types/slaConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, RefreshCw, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SLAListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const urlQuery = searchParams.get("q") ?? "";

  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [query, setQuery] = useState(urlQuery);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const {
    data: slaConfigs,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sla-configurations", page, pageSize, query],
    queryFn: () =>
      SLAConfigurationService.getAllConfigurations({
        page,
        pageSize,
        search: query,
      }),
  });

  const gridData = slaConfigs?.results ?? [];
  const total = slaConfigs?.count ?? 0;

  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    try {
      await SLAConfigurationService.deleteConfiguration(id);
      refetch();
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setIsDeleting(null);
    }
  };

  const columns: ProColumns<SLAConfiguration>[] = [
    {
      title: "SLA Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
      render: (_, record) => (
        <button
          className="font-medium text-primary hover:underline"
          onClick={() => navigate(navigationRoutes.SLA_EDIT(record.id))}
        >
          {record.name}
        </button>
      ),
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => (
        <ActiveToggleButton
          table={tableName.sla_configuration}
          pk={record.id}
          is_active={record.enabled ?? false}
        />
      ),
    },
    {
      title: "Actions",
      width: 140,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => navigate(navigationRoutes.SLA_EDIT(record.id))}
          type="text"
          title="Edit SLA"
          className="!p-1 hover:!bg-muted"
        />,
        <Popconfirm
          key="delete"
          title="Delete this SLA configuration?"
          description="This action cannot be undone."
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete SLA"
            loading={isDeleting === record.id}
            className="!p-1"
          />
        </Popconfirm>,
      ],
    },
  ];

  const toolBarRender = () => [
    <button
      key="refresh"
      onClick={() => refetch()}
      className="rounded-lg border border-border p-2 transition-colors hover:bg-muted"
      title="Refresh"
    >
      <RefreshCw size={16} className="text-muted-foreground" />
    </button>,
    <CustomButton
      key="add"
      type="primary"
      onClick={() => navigate(navigationRoutes.SLA_ADD)}
      title="Add SLA"
      className="!rounded-lg !px-4 !py-2 !text-sm !font-medium"
    >
      <Plus size={16} className="mr-1" /> Add SLA
    </CustomButton>,
  ];

  return (
    <div className="sla-list-page">
      <BaseTable<SLAConfiguration>
        loading={isLoading}
        columns={columns}
        dataSource={gridData}
        page={page}
        pageSize={pageSize}
        total={total}
        rowKey="id"
        tableName={gridName.slaConfigurationTable}
        search={false}
        manualSearch
        searchText={query}
        onSearchChange={val => {
          setPage(1);
          setQuery(val);
        }}
        onSearchSubmit={val => {
          setPage(1);
          setQuery(val);
          searchParams.set("page", "1");
          searchParams.set("pageSize", pageSize.toString());
          searchParams.set("q", val);
          navigate(
            `${navigationRoutes.SLA_CONFIGURATIONS}?${searchParams.toString()}`
          );
        }}
        searchPlaceholder="Search SLA"
        toolBarRender={toolBarRender}
        headerTitle="SLA"
        onPageChange={(newPage, size) => {
          setPage(newPage);
          setPageSize(size ?? 10);
          searchParams.set("page", newPage.toString());
          searchParams.set("pageSize", size?.toString() ?? "10");
          navigate(
            `${navigationRoutes.SLA_CONFIGURATIONS}?${searchParams.toString()}`
          );
        }}
        request={() => refetch()}
      />
    </div>
  );
};

export default SLAListPage;
